/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import Point from '@arcgis/core/geometry/Point';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import isArray from 'lodash.isarray';

/**
 * @useZoomTo
 *
 * Provides a hook for retrieving an extent.
 *
 * ```typescript
 *
 *    const zoomTo = useZoomTo();
 *    ...
 *    Get the expanded extent from array of points
 *    const extent = zoomTo?.current(points, 1.2);
 *
 * ```
 */
export const useZoomTo = () => {
  const zoomTo = useRef(null);
  useEffect(() => {
    zoomTo.current = function(points: any[], factor: number): __esri.Extent {
      const multipoint = new Multipoint(new SpatialReference({wkid: 4326}));
      points?.forEach(p => {
        const coordinates = isArray(p) ? p : (p?.geometry?.coordinates || p?.coordinates);
        multipoint.addPoint(new Point(coordinates[0], coordinates[1]));
      });
      return multipoint.extent ? multipoint.extent.expand(factor) : null;
    };
  }, []);
  return function(points: any[], factor: number): __esri.Extent {
    return zoomTo?.current ? zoomTo.current(points, factor) : null;
  };
};

export default useZoomTo;
